import React, { useState, useEffect } from 'react';
import VdoPlayer from './VdoPlayer.tsx';
import { Box, Button, Grid, Text, AlertDialog, Flex } from '@radix-ui/themes';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../api/axios.js';

interface Lesson {
    id: number;
    name: string;
    description: string;
    vdo_file: string;
}

interface ExamStatus {
    hasTakenExam: boolean;
    score: number | null;
    fullScore: number | null;
    isRetest: boolean;
    times?: number;
}

const Lesson = () => {
    const { id: lessonId } = useParams();
    const [lesson, setLesson] = useState<Lesson | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [examStatus, setExamStatus] = useState<ExamStatus>({ 
        hasTakenExam: false, 
        score: null, 
        fullScore: null,
        isRetest: false,
        times: 0
    });
    const navigate = useNavigate();
    const user = JSON.parse(localStorage.getItem('user') || '{}');

    useEffect(() => {
        const fetchData = async () => {
            try {
                const lessonResponse = await api.get(`/lessons/${lessonId}`);
                setLesson(lessonResponse.data);

                // Record the lesson view
                if (user.id) {
                    try {
                        await api.post(`/lessons/${lessonId}/views`, {
                            userId: user.id
                        });
                    } catch (viewError) {
                        console.error('Error recording lesson view:', viewError);
                    }
                }

                // Check if user has taken the exam
                if (user.id) {
                    const examStatusResponse = await api.get(
                        `/exams/check/${lessonId}/${user.id}`
                    );
                    setExamStatus(examStatusResponse.data);
                }

                setLoading(false);
            } catch (err) {
                console.error('Error fetching data:', err);
                setError('Failed to load lesson');
                setLoading(false);
            }
        };
        fetchData();
    }, [lessonId, user.id]);

    if (loading) return <div>Loading...</div>;
    if (error) return <div>Error: {error}</div>;
    if (!lesson) return <div>Lesson not found</div>;

    const handleTakeExam = (lessonId) => {
        return (
          <AlertDialog.Root>
            <AlertDialog.Trigger>
              <Button style={{ cursor: 'pointer' }}>Take an exam</Button>
            </AlertDialog.Trigger>
            <AlertDialog.Content>
              <AlertDialog.Title>Start Exam</AlertDialog.Title>
              <AlertDialog.Description>
                Are you ready to start the exam? Make sure you have reviewed the lesson material.
              </AlertDialog.Description>
              <div style={{ display: 'flex', gap: '16px', marginTop: '20px' }}>
                <AlertDialog.Cancel>
                  <Button variant="soft" color="gray">
                    Cancel
                  </Button>
                </AlertDialog.Cancel>
                <AlertDialog.Action>
                  <Button 
                    variant="solid" 
                    color="blue" 
                    onClick={() => navigate(`/exam/${lessonId}`)}
                  >
                    Start Exam
                  </Button>
                </AlertDialog.Action>
              </div>
            </AlertDialog.Content>
          </AlertDialog.Root>
        );
    };

    return (
        <div id="lesson" style={{ padding: '20px 100px' }}>
            <div className='lesson-item'>
                <div className="vdo-container" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'top' }}>
                    <div className="vdo-player" style={{ paddingRight: '30px' }}>
                        <VdoPlayer videoUrl={lesson.vdo_file} />
                    </div>
                    <div className="exam-button" style={{ textAlign: 'right' }}>
                        <Flex gap="3" mt="4" justify="end">
                            <Button 
                                variant="solid" 
                                color="blue" 
                                onClick={() => navigate(`/exam/${lessonId}`)}
                                disabled={examStatus.hasTakenExam && !examStatus.isRetest}
                            >
                                {examStatus.hasTakenExam 
                                    ? examStatus.isRetest 
                                        ? 'เริ่มสอบซ่อม' 
                                        : 'ทำข้อสอบเสร็จสิ้น'
                                    : 'เริ่มทำข้อสอบ'
                                }
                            </Button>
                            {examStatus.hasTakenExam && (
                                <Flex direction="column" gap="1">
                                    <Text size="2" style={{ color: '#666' }}>
                                        คะแนนสอบ: {examStatus.score}/{examStatus.fullScore}
                                    </Text>
                                    <Text size="2" style={{ color: '#666' }}>
                                        ครั้งที่ทำข้อสอบ: {examStatus.times || 1}
                                    </Text>
                                </Flex>
                            )}
                        </Flex>
                        {!examStatus.hasTakenExam && (
                          <div>
                            <p>When you are ready, click the button</p>
                            <p>above to take an exam.</p>
                          </div>
                        )}
                    </div>
                </div>
                <Box mt="4" align="left">
                    <Text size="5" weight="bold" mb="2">
                        {lesson.name}
                    </Text><br />
                    <Text size="3" color="gray">
                        {lesson.description}
                    </Text>
                    {/* <Text size="2" color="gray" mt="2">
                        Lesson Code: {lesson.lesson_code}
                    </Text> */}
                </Box>
            </div>
        </div>
    );
};

export default Lesson;